<template>
    <ValidationObserver ref="observer" tag="section" class="tw:flex tw:flex-col tw:gap-4" v-slot="{ invalid }">
        <CardFront v-if="!loading" :titleNotBorder="$t('perfil.informacion_personal')">
            <div class="tw:flex tw:flex-col tw:gap-4 tw:sm:flex-row">
                <div class="tw:w-full">
                    <span v-if="!isEditing" class="tw:text-xs tw:font-semibold tw:text-slate-400 tw:uppercase tw:tracking-wide">{{ $t('perfil.nombre_y_apellidos') }}</span>
                    <p v-if="!isEditing" data-test="display-nombre" class="tw:m-0 tw:font-medium tw:text-slate-900 tw:mt-1">{{ props.userData.name }}&nbsp;{{ props.userData.surnames }}</p>
                    <div v-else class="tw:flex tw:flex-col tw:gap-4 ">
                        <InputText :label="$t('perfil.nombre')" name="nombre" v-model="localData.name" rules="required"/>
                        <InputText :label="$t('perfil.apellidos')" name="apellidos" v-model="localData.surnames" rules="required"/>
                    </div>
                </div>
                <div class="tw:flex tw:justify-between tw:items-start tw:w-full" :class="{ 'tw:flex-col tw:gap-4': isEditing }">
                    <div class="tw:w-full">
                        <span v-if="!isEditing" class="tw:text-xs tw:font-semibold tw:text-slate-400 tw:uppercase tw:tracking-wide">{{ $t('formularios.nacimiento') }}</span>
                        <p v-if="!isEditing" class="tw:m-0 tw:mt-1 tw:font-medium tw:text-slate-900">{{ fechaNacimientoFormateada ?? '-' }}</p>
                        <div v-else class="tw:w-full">
                            <InputText
                                :label="$t('formularios.nacimiento')"
                                name="fecha_nacimiento"
                                type="date"
                                v-model="localData.fecha_nacimiento"
                            />
                        </div>
                    </div>
                    <div class="tw:w-full tw:text-center tw:md:text-left">
                        <span v-if="!isEditing" class="tw:text-xs tw:font-semibold tw:text-slate-400 tw:uppercase tw:tracking-wide">{{ $t('perfil.dni') }}</span>
                        <p v-if="!isEditing" data-test="display-nif" class="tw:m-0 tw:mt-1 tw:font-medium tw:text-slate-900">{{ props.userData.nif || '-' }}</p>
                        <div v-else class="tw:w-full">
                            <InputText
                                :label="$t('perfil.dni')"
                                name="nif"
                                :rules="{ identificacion: { tipo: 'DNI' } }"
                                v-model="localData.nif"
                                :apiValidationError="errors?.nif"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </CardFront>
        <Skeleton v-if="loading">
            <template #skeleton>
                <SkeletonItem class="tw:w-[170px] tw:mb-6" />
                <div class="tw:flex tw:flex-col tw:md:flex-row tw:gap-6 tw:md:mb-[2px]">
                    <div class="tw:flex tw:w-full tw:flex-col tw:gap-2">
                        <SkeletonItem class="tw:w-[150px]" />
                        <SkeletonItem class="tw:w-[180px]" />
                    </div>
                    <div class="tw:flex tw:w-full">
                        <div class="tw:flex tw:flex-col tw:w-1/2 tw:gap-2">
                            <SkeletonItem class="tw:w-[150px]" />
                            <SkeletonItem class="tw:w-[100px]" />
                        </div>
                        <div class="tw:flex tw:flex-col tw:w-1/2 tw:gap-2 tw:items-center tw:md:items-start">
                            <SkeletonItem class="tw:w-[60px]" />
                            <SkeletonItem class="tw:w-[60px]" />
                        </div>
                    </div>
                </div>
            </template>
        </Skeleton>
        <CardFront v-if="!loading" :titleNotBorder="$t('perfil.detalles_contacto')">
            <div class="tw:flex tw:flex-col tw:gap-2">
                <div class="tw:flex tw:flex-col tw:gap-4 tw:sm:flex-row">
                    <div class="tw:w-full">
                        <span v-if="!isEditing" class="tw:text-xs tw:font-semibold tw:text-slate-400 tw:uppercase tw:tracking-wide">{{ $t('formularios.email') }}</span>
                        <p v-if="!isEditing" class="tw:m-0 tw:mt-1 tw:font-medium tw:text-slate-900">{{ props.userData.email }}</p>
                        <div v-else class="tw:w-full">
                            <BaseInputEmail :label="$t('formularios.email')" name="email" v-model="localData.email" rules="required" :apiValidationError="errors?.email" />
                        </div>
                    </div>
                    <div class="tw:w-full">
                        <span v-if="!isEditing" class="tw:text-xs tw:font-semibold tw:text-slate-400 tw:uppercase tw:tracking-wide">{{ $t('perfil.telefono') }}</span>
                        <p v-if="!isEditing" class="tw:m-0 tw:mt-1 tw:font-medium tw:text-slate-900">{{ telefonoFormateado }}</p>
                        <div v-else class="tw:w-full">
                            <BaseInputPhone :label="$t('perfil.telefono')" v-model="localData.telefono" :required="true" :forceValidate="submitted" @valid="phoneIsValid = $event" />
                        </div>
                    </div>
                </div>
            </div>
        </CardFront>
        <Skeleton v-if="loading">
            <template #skeleton>
                <SkeletonItem class="tw:w-[160px] tw:mb-6" />
                <div class="tw:flex tw:flex-col tw:sm:flex-row tw:gap-5">
                    <div class="tw:w-full tw:flex tw:flex-col tw:gap-2.5">
                        <SkeletonItem class="tw:w-[50px]" />
                        <SkeletonItem class="tw:w-[240px]" />
                    </div>
                    <div class="tw:w-full tw:flex tw:flex-col tw:gap-2.5">
                        <SkeletonItem class="tw:w-[75px]" />
                        <SkeletonItem class="tw:w-[100px]" />
                    </div>
                </div>
            </template>
        </Skeleton>
        <CardFront v-if="!loading" :titleNotBorder="$t('perfil.direccion')">
            <div class="tw:flex tw:flex-col tw:gap-2">
                <div v-if="!isEditing" class="tw:flex tw:flex-col tw:md:flex-row tw:gap-2">
                    <div class="tw:w-full">
                        <span class="tw:text-xs tw:font-semibold tw:text-slate-400 tw:uppercase tw:tracking-wide">{{ $t('formularios.pais') }}</span>
                        <p class="tw:m-0 tw:mt-1 tw:font-medium tw:text-slate-900">{{ props.userData.pais?.nombre || '-' }}</p>
                    </div>
                    <div class="tw:flex tw:w-full">
                        <div class="tw:w-full">
                            <span class="tw:text-xs tw:font-semibold tw:text-slate-400 tw:uppercase tw:tracking-wide">{{ $t('formularios.provincia') }}</span>
                            <p class="tw:m-0 tw:mt-1 tw:font-medium tw:text-slate-900">{{ props.userData.provincia?.nombre || '-' }}</p>
                        </div>
                        <div class="tw:w-full tw:text-center tw:sm:text-left">
                            <span class="tw:text-xs tw:font-semibold tw:text-slate-400 tw:uppercase tw:tracking-wide">{{ $t('formularios.localidad') }}</span>
                            <p class="tw:m-0 tw:mt-1 tw:font-medium tw:text-slate-900">{{ props.userData.localidad?.nombre || '-' }}</p>
                        </div>
                    </div>
                </div>
                <div v-else class="tw:flex tw:flex-col tw:sm:flex-row tw:gap-4">
                    <InputMulti
                        class="tw:w-full tw:md:w-1/3"
                        :label="$t('formularios.pais')"
                        name="pais"
                        :loading="loadingPaises"
                        :value="paisId"
                        :options="paises"
                        valueKey="id"
                        labelKey="nombre"
                        :multiple="false"
                        :disabled="loadingPaises"
                        :apiValidationError="errors?.pais"
                        @input="seleccionarPais"
                        @validation-error="submitted = true"
                    />
                    <InputMulti
                        v-if="esEspana"
                        class="tw:w-full tw:md:w-1/3"
                        :label="$t('formularios.provincia')"
                        name="provincia"
                        :loading="loadingProvincias"
                        :value="provinciaId"
                        :options="provincias"
                        valueKey="id"
                        labelKey="nombre"
                        :multiple="false"
                        :disabled="!paisId || loadingProvincias"
                        :apiValidationError="errors?.provincia"
                        @input="seleccionarProvincia"
                        @validation-error="submitted = true"
                    />
                    <InputMulti
                        v-if="esEspana"
                        class="tw:w-full tw:md:w-1/3"
                        :label="$t('formularios.localidad')"
                        name="localidad"
                        :loading="loadingLocalidades"
                        :value="localidadId"
                        :options="localidades"
                        valueKey="id"
                        labelKey="nombre"
                        :multiple="false"
                        :disabled="!provinciaId || loadingLocalidades"
                        :apiValidationError="errors?.localidad"
                        @input="seleccionarLocalidad"
                        @validation-error="submitted = true"
                    />
                </div>
            </div>
        </CardFront>
        <Skeleton v-if="loading">
            <template #skeleton>
                <SkeletonItem class="tw:w-[100px] tw:mb-[23px] tw:md:mb-[23px] tw:md:mt-[2px]" />
                <div class="tw:flex tw:flex-col tw:md:flex-row tw:gap-[17px] tw:md:mb-[1px]">
                    <div class="tw:w-full tw:flex tw:flex-col tw:gap-2">
                        <SkeletonItem class="tw:w-[35px]" />
                        <SkeletonItem class="tw:w-[60px]" />
                    </div>
                    <div class="tw:flex tw:flex-row tw:sm:w-full">
                        <div class="tw:w-full tw:flex tw:flex-col tw:gap-2">
                            <SkeletonItem class="tw:w-[90px]" />
                            <SkeletonItem class="tw:w-[60px]" />
                        </div>
                        <div class="tw:w-full tw:flex tw:flex-col tw:items-center tw:sm:items-start tw:gap-2">
                            <SkeletonItem class="tw:w-[100px]" />
                            <SkeletonItem class="tw:w-[90px]" />
                        </div>
                    </div>
                </div>
            </template>
        </Skeleton>
        <div v-if="!isEditing" class="tw:flex tw:justify-end">
            <Button v-if="!loading" data-test="btn-editar" variant="alt" icon-name="edit" icon-name-class="tw:text-sm" @click="startEditing()">
                {{ $t('perfil.editar') }}
            </Button>
            <Skeleton v-if="loading" customClass="tw:flex-row tw:py-2 tw:px-3 tw:border tw:border-gray-200">
                <template #skeleton>
                    <SkeletonItem class="tw:w-[15px]" height="tw:h-3"/>
                    <SkeletonItem class="tw:w-[30px]" height="tw:h3"/>
                </template>
            </Skeleton>

        </div>
        <div v-else class="tw:flex tw:justify-end tw:gap-2">
            <Button type="button" data-test="btn-cancelar" variant="default" icon-name="close" icon-name-class="tw:text-sm" @click="handleCancel()">
                {{ $t('perfil.cancelar') }}
            </Button>
            <Button type="button" data-test="btn-guardar" variant="alt" icon-name="save" icon-name-class="tw:text-sm" :disabled="submitted && (invalid || !phoneIsValid)" @click="handleSave()">
                {{ $t('perfil.guardar') }}
            </Button>
        </div>
    </ValidationObserver>
</template>

<script setup>
    import { ref, inject, toRef } from 'vue';
    import CardFront from '@/modules/front/ui/CardFront.vue';
    import InputText from '@/modules/front/ui/InputText.vue';
    import InputMulti from '@/modules/front/ui/InputMulti.vue';
    import BaseInputEmail from '@/modules/front/ui/BaseInputEmail.vue';
    import BaseInputPhone from '@/modules/front/ui/BaseInputPhone.vue';
    import { Skeleton, SkeletonItem } from '@/modules/front/ui/skeleton';
    import Button from '@/modules/front/ui/Button.vue';
    import { useDatosPersonalesForm } from '@/composables/front/perfil/useDatosPersonalesForm';
    import { PERFIL_EDITING_KEY } from '@/composables/front/perfil/keys';

    const props = defineProps({
        userData: {
            type: Object,
            required: true,
        }
    });

    const emit = defineEmits(['saved']);
    const observer = ref(null);
    const perfilState = inject(PERFIL_EDITING_KEY, null) || inject('perfilState', null);

    const {
        isEditing, localData, loading, errors, startEditing,
        paises, provincias, localidades,
        loadingPaises, loadingProvincias, loadingLocalidades,
        paisId, provinciaId, localidadId,
        esEspana, seleccionarPais, seleccionarProvincia, seleccionarLocalidad,
        fechaNacimientoFormateada,
        telefonoFormateado,
        submitted, phoneIsValid,
        handleSave, handleCancel,
    } = useDatosPersonalesForm({ userData: toRef(props, 'userData'), perfilState, observer, emit });
</script>
